import { ChronoUnit, LocalDate, LocalDateTime, LocalTime } from 'js-joda';
import { FormSelectField } from 'bento';
import { SelectFieldProps } from 'bento/SelectField';
import { concat, filter, first, isEmpty, last } from 'es-toolkit/compat';
import {
  getTimeFormatter,
  twentyFourHourFormatter,
  twentyFourHourNakedFormatter,
} from '@tock/i18n';
import { useAppContext } from '@tock/coretypes';
import AppSettings from 'settings/AppSettings';

export interface FormTimeSelectFieldProps
  extends Pick<SelectFieldProps, Exclude<keyof SelectFieldProps, 'children'>> {
  includeNone?: boolean;
  /**
   * How to increment the time options, in minutes.
   * Please specify this as a positive integer, and as one that divides the difference between
   * minLocalTimeInclusive and maxLocalTimeInclusive evenly.
   * Note that if it is not specified or specified as 0, it defaults to 15 minutes.
   */
  incrementsInMinutes?: number;
  /**
   * The first selectable time. It may be after the last selectable time; in this case, the component
   * will wrap around at 00:00 (midnight) and continue until maxLocalTimeInclusive.
   * It may also be equal to the last selectable time; in this case, that will be the only selectable time.
   */
  minLocalTimeInclusive: LocalTime;
  /**
   * The last selectable time.
   */
  maxLocalTimeInclusive: LocalTime;
  ['aria-label']?: string;
}

const FIXED_DAY = LocalDate.of(2010, 1, 15); // Arbitrary date to use for LocalDateTime construct.
const DAY_AFTER_FIXED_DAY = LocalDate.of(2010, 1, 16); // This is used to halt option generation in the case that maxLocalTimeInclusive is before minLocalTimeInclusive.

export function getInclusiveTimesFromTimeRange(
  timeRange: LocalTime[],
  minutesToSubtractFromMax = 15
) {
  const minLocalTimeInclusive =
    first(timeRange) || LocalTime.parse(AppSettings.LAST_TIME_IN_BUSINESS_DAY);
  const maxLocalTimeInclusive =
    last(timeRange)?.minusMinutes(minutesToSubtractFromMax) ||
    LocalTime.parse(AppSettings.LAST_TIME_IN_BUSINESS_DAY);
  return { minLocalTimeInclusive, maxLocalTimeInclusive };
}

/**
 * @deprecated Deprecated — use pantry TimeSelect instead.
 * @see https://docs.tocktix.com/pantry?path=/docs/components-timeselect--docs
 * Migration: Replace FormTimeSelectField with the pantry TimeSelect component wired to your form library.
 */
export const FormTimeSelectField: React.FunctionComponent<
  React.PropsWithChildren<FormTimeSelectFieldProps>
> = ({
  includeNone,
  minLocalTimeInclusive,
  maxLocalTimeInclusive,
  incrementsInMinutes,
  ...props
}) => {
  const {
    context: { locale },
  } = useAppContext();

  const deltaMinutes = incrementsInMinutes || 15;
  const options: SelectOption[] = [];

  // This structure is a good way to handle iteration because we want the loop to execute *once* in the event
  // that minLocalTimeInclusive and maxLocalTimeInclusive are the same.
  //
  // And we want the loop to execute many times if, e.g., minLocalTimeInclusive is 00:00, maxLocalTimeInclusive is 23:45,
  // and incrementsInMinutes is 15. If we im˚plement only in terms of LocalTime, not LocalDateTime, it is easy for
  // the wraparound to result in the loop body never executing at all.
  //
  // Finally, we want to correct whatever the sender included to be defensive against cases
  // where there are trailing seconds/millis; the component always truncates to whole minutes.
  minLocalTimeInclusive = minLocalTimeInclusive.truncatedTo(ChronoUnit.MINUTES);
  maxLocalTimeInclusive = maxLocalTimeInclusive.truncatedTo(ChronoUnit.MINUTES);

  const minLocalTimeWithDate = LocalDateTime.of(FIXED_DAY, minLocalTimeInclusive);
  const maxLocalTimeWithDate = LocalDateTime.of(
    maxLocalTimeInclusive.isBefore(minLocalTimeInclusive) ? DAY_AFTER_FIXED_DAY : FIXED_DAY,
    maxLocalTimeInclusive
  );

  for (
    let dateTime = minLocalTimeWithDate;
    !dateTime.isAfter(maxLocalTimeWithDate);
    dateTime = dateTime.plusMinutes(deltaMinutes)
  ) {
    const t = dateTime.toLocalTime();
    options.push({
      key: twentyFourHourNakedFormatter.format(t),
      label: getTimeFormatter(locale).format(t),
      value: twentyFourHourFormatter.format(t),
    });
  }

  // Some forms that use this time selector need both a start and end time
  // so I prefer to not display none as the first option
  const defaultSelectOptionLabel = props.SelectProps?.placeholder ?? 'None';
  const selectOptions = includeNone
    ? concat(
        [
          {
            key: 'None',
            label: defaultSelectOptionLabel,
            value: '',
          },
        ],
        options
      )
    : options;
  return (
    <FormSelectField disabled={false} {...props}>
      {selectOptions.map((selectOption) => (
        <option value={selectOption.value} key={selectOption.key} data-testid={selectOption.value}>
          {selectOption.label}
        </option>
      ))}
    </FormSelectField>
  );
};

export const getStartTimeInputProps = (
  date?: LocalDate | LocalDate[],
  endTime?: string
): Pick<FormTimeSelectFieldProps, 'minLocalTimeInclusive' | 'maxLocalTimeInclusive'> => {
  const dateList = date instanceof LocalDate ? [date] : date;
  return {
    minLocalTimeInclusive:
      dateList && getDatesIncludesCurrent(dateList)
        ? LocalTime.now().plusMinutes(15 - (LocalTime.now().minute() % 15))
        : LocalTime.parse('04:00'),
    maxLocalTimeInclusive: endTime
      ? LocalTime.parse(endTime).minusMinutes(15)
      : LocalTime.parse('03:45'),
  };
};

export const getEndTimeInputProps = (
  date?: LocalDate | LocalDate[],
  startTime?: string
): Pick<FormTimeSelectFieldProps, 'minLocalTimeInclusive' | 'maxLocalTimeInclusive'> => {
  const dateList = date instanceof LocalDate ? [date] : date;
  return {
    minLocalTimeInclusive: startTime
      ? LocalTime.parse(startTime).plusMinutes(15)
      : dateList && getDatesIncludesCurrent(dateList)
        ? LocalTime.now().plusMinutes(15 - (LocalTime.now().minute() % 15))
        : LocalTime.parse('04:00'),
    maxLocalTimeInclusive: LocalTime.parse('03:45'),
  };
};

const getDatesIncludesCurrent = (dates: LocalDate[]) => {
  const currentServiceDate = LocalDateTime.now().minusHours(4).toLocalDate();
  return !isEmpty(filter(dates, (date) => date.equals(currentServiceDate)));
};
